import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Trash2, Plus, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";

const STATUS_STYLES = {
    pending: "bg-amber-100 text-amber-700 border-amber-300",
    confirmed: "bg-emerald-100 text-emerald-700 border-emerald-300",
    completed: "bg-slate-100 text-slate-600 border-slate-300",
    cancelled: "bg-red-100 text-red-600 border-red-300",
};

const emptyService = { name: "", description: "", price: "" };

export default function AdminDashboard() {
    const navigate = useNavigate();
    const [bookings, setBookings] = useState([]);
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newService, setNewService] = useState(emptyService);
    const [editingId, setEditingId] = useState(null);
    const [editService, setEditService] = useState(emptyService);

    const logout = useCallback(() => {
        localStorage.removeItem("pv_token");
        navigate("/admin/login");
    }, [navigate]);

    const load = useCallback(async () => {
        try {
            await api.get("/auth/me");
            const [b, s] = await Promise.all([api.get("/bookings"), api.get("/services/all")]);
            setBookings(b.data);
            setServices(s.data);
        } catch {
            logout();
        } finally {
            setLoading(false);
        }
    }, [logout]);

    useEffect(() => { load(); }, [load]);

    const setStatus = async (id, status) => {
        try {
            const { data } = await api.patch(`/bookings/${id}`, { status });
            setBookings((bs) => bs.map((b) => (b.id === id ? data : b)));
            toast.success(`Booking marked ${status}`);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const removeBooking = async (id) => {
        try {
            await api.delete(`/bookings/${id}`);
            setBookings((bs) => bs.filter((b) => b.id !== id));
            toast.success("Booking deleted");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const addService = async (e) => {
        e.preventDefault();
        try {
            const { data } = await api.post("/services", newService);
            setServices((ss) => [...ss, data]);
            setNewService(emptyService);
            toast.success("Service added");
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const saveService = async (id) => {
        try {
            const { data } = await api.put(`/services/${id}`, editService);
            setServices((ss) => ss.map((s) => (s.id === id ? data : s)));
            setEditingId(null);
            toast.success("Service updated");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const removeService = async (id) => {
        try {
            await api.delete(`/services/${id}`);
            setServices((ss) => ss.filter((s) => s.id !== id));
            toast.success("Service deleted");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const fieldCls = "bg-white border-slate-300 text-indigo-950 h-10 rounded-none focus-visible:ring-2 focus-visible:ring-purple-500 placeholder:text-slate-500";

    if (loading) {
        return <div className="min-h-screen bg-white flex items-center justify-center text-slate-500 text-sm tracking-[0.2em] uppercase" data-testid="admin-loading">Loading…</div>;
    }

    return (
        <div className="min-h-screen bg-white" data-testid="admin-dashboard">
            <header className="border-b border-indigo-100 bg-white/90 backdrop-blur-xl sticky top-0 z-40">
                <div className="max-w-7xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <img src="/assets/logo.png" alt="Planet Valet logo" className="h-9 w-auto" />
                        <span className="text-xs tracking-[0.2em] uppercase font-semibold text-slate-500">Owner area</span>
                    </div>
                    <button onClick={logout} className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-semibold text-slate-500 hover:text-purple-600 transition-colors" data-testid="admin-logout-button">
                        <LogOut className="w-4 h-4" /> Sign out
                    </button>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 sm:px-10 py-12">
                <h1 className="font-serif-display text-3xl sm:text-4xl text-indigo-950 mb-10" data-testid="admin-heading">Booking requests</h1>
                <Tabs defaultValue="bookings">
                    <TabsList className="bg-indigo-50/50 border border-indigo-100 rounded-none mb-8">
                        <TabsTrigger value="bookings" className="rounded-none data-[state=active]:bg-blue-600 data-[state=active]:text-white text-xs tracking-[0.15em] uppercase font-semibold" data-testid="tab-bookings">
                            Bookings ({bookings.length})
                        </TabsTrigger>
                        <TabsTrigger value="services" className="rounded-none data-[state=active]:bg-blue-600 data-[state=active]:text-white text-xs tracking-[0.15em] uppercase font-semibold" data-testid="tab-services">
                            Services ({services.length})
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="bookings">
                        {bookings.length === 0 ? (
                            <p className="text-slate-500 font-light py-16 text-center" data-testid="bookings-empty">No booking requests yet. They'll appear here as soon as someone books.</p>
                        ) : (
                            <div className="border border-indigo-100 overflow-x-auto">
                                <Table data-testid="bookings-table">
                                    <TableHeader>
                                        <TableRow className="border-indigo-100 hover:bg-transparent">
                                            <TableHead className="text-slate-500 text-xs uppercase tracking-widest">Date wanted</TableHead>
                                            <TableHead className="text-slate-500 text-xs uppercase tracking-widest">Customer</TableHead>
                                            <TableHead className="text-slate-500 text-xs uppercase tracking-widest">Service</TableHead>
                                            <TableHead className="text-slate-500 text-xs uppercase tracking-widest">Notes</TableHead>
                                            <TableHead className="text-slate-500 text-xs uppercase tracking-widest">Status</TableHead>
                                            <TableHead className="text-slate-500 text-xs uppercase tracking-widest text-right">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {bookings.map((b) => (
                                            <TableRow key={b.id} className="border-indigo-100 hover:bg-indigo-50/50" data-testid={`booking-row-${b.id}`}>
                                                <TableCell className="text-indigo-900 whitespace-nowrap">{b.preferred_date}</TableCell>
                                                <TableCell>
                                                    <div className="text-indigo-950 font-medium">{b.name}</div>
                                                    <div className="text-slate-500 text-sm">{b.phone}</div>
                                                    {b.email && <div className="text-slate-500 text-sm">{b.email}</div>}
                                                </TableCell>
                                                <TableCell className="text-slate-600 whitespace-nowrap">{b.service_name}</TableCell>
                                                <TableCell className="text-slate-500 text-sm max-w-[220px] truncate">{b.notes || "—"}</TableCell>
                                                <TableCell>
                                                    <Select value={b.status} onValueChange={(v) => setStatus(b.id, v)}>
                                                        <SelectTrigger className={`w-[140px] h-9 rounded-none border text-xs uppercase tracking-wider font-semibold ${STATUS_STYLES[b.status]}`} data-testid={`booking-status-${b.id}`}>
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent className="bg-white border-indigo-100 text-indigo-950 rounded-none">
                                                            {Object.keys(STATUS_STYLES).map((s) => (
                                                                <SelectItem key={s} value={s} className="capitalize" data-testid={`status-option-${b.id}-${s}`}>{s}</SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <button onClick={() => removeBooking(b.id)} className="text-slate-500 hover:text-red-600 transition-colors" data-testid={`booking-delete-${b.id}`}>
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </TabsContent>

                    <TabsContent value="services">
                        <form onSubmit={addService} className="border border-indigo-100 bg-indigo-50/40 p-6 mb-8 grid grid-cols-1 sm:grid-cols-[1fr_2fr_1fr_auto] gap-4 items-end" data-testid="service-add-form">
                            <Input required placeholder="Service name" value={newService.name} onChange={(e) => setNewService((s) => ({ ...s, name: e.target.value }))} className={fieldCls} data-testid="service-add-name" />
                            <Input placeholder="Short description" value={newService.description} onChange={(e) => setNewService((s) => ({ ...s, description: e.target.value }))} className={fieldCls} data-testid="service-add-description" />
                            <Input placeholder="e.g. from £120" value={newService.price} onChange={(e) => setNewService((s) => ({ ...s, price: e.target.value }))} className={fieldCls} data-testid="service-add-price" />
                            <button type="submit" className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-xs tracking-[0.15em] uppercase font-bold px-5 h-10 transition-colors" data-testid="service-add-button">
                                <Plus className="w-4 h-4" /> Add
                            </button>
                        </form>
                        <div className="border border-indigo-100" data-testid="services-admin-list">
                            {services.map((s) => (
                                <div key={s.id} className="border-b border-white/5 last:border-b-0 p-5" data-testid={`service-row-${s.id}`}>
                                    {editingId === s.id ? (
                                        <div className="grid grid-cols-1 sm:grid-cols-[1fr_2fr_1fr_auto] gap-4 items-center">
                                            <Input value={editService.name} onChange={(e) => setEditService((x) => ({ ...x, name: e.target.value }))} className={fieldCls} data-testid={`service-edit-name-${s.id}`} />
                                            <Input value={editService.description} onChange={(e) => setEditService((x) => ({ ...x, description: e.target.value }))} className={fieldCls} data-testid={`service-edit-description-${s.id}`} />
                                            <Input value={editService.price} onChange={(e) => setEditService((x) => ({ ...x, price: e.target.value }))} className={fieldCls} data-testid={`service-edit-price-${s.id}`} />
                                            <div className="flex gap-3">
                                                <button onClick={() => saveService(s.id)} className="text-emerald-600 hover:text-emerald-500" data-testid={`service-save-${s.id}`}><Check className="w-5 h-5" /></button>
                                                <button onClick={() => setEditingId(null)} className="text-slate-500 hover:text-slate-600" data-testid={`service-cancel-${s.id}`}><X className="w-5 h-5" /></button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex items-center justify-between gap-6">
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-3 flex-wrap">
                                                    <span className="text-indigo-950 font-medium">{s.name}</span>
                                                    <span className="text-xs tracking-[0.2em] uppercase font-bold text-purple-600">{s.price}</span>
                                                </div>
                                                <p className="text-slate-500 text-sm mt-1 truncate">{s.description}</p>
                                            </div>
                                            <div className="flex gap-4 shrink-0">
                                                <button onClick={() => { setEditingId(s.id); setEditService({ name: s.name, description: s.description, price: s.price }); }} className="text-slate-500 hover:text-purple-600 transition-colors" data-testid={`service-edit-${s.id}`}>
                                                    <Pencil className="w-4 h-4" />
                                                </button>
                                                <button onClick={() => removeService(s.id)} className="text-slate-500 hover:text-red-600 transition-colors" data-testid={`service-delete-${s.id}`}>
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </TabsContent>
                </Tabs>
            </main>
        </div>
    );
}
